import { AccountTypes } from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';

export const BROKER_INVOICES_EXIST_TOOLTIP = (
	'Broker invoices already exist for this broker on the Commissions tab. ' +
	'Delete the broker invoices first.'
);

export type BrokerEditabilityRow = {
	id?: number | null;
	brokerId?: number | null;
	paidBy?: Values<typeof AccountTypes>;
};

export type BrokerEditabilityContext = {
	brokerInvoiceCountByBrokerId: Record<number, number>;
};

export type PaidByOption = {
	label: string;
	value: Values<typeof AccountTypes>;
	disabled: boolean;
	title?: string;
};

const getRowReasons = (
	row: BrokerEditabilityRow | null | undefined,
	ctx: BrokerEditabilityContext,
) => {
	const isNewRow = row?.id == null;
	const hasBrokerInvoices = row?.brokerId != null &&
		(ctx.brokerInvoiceCountByBrokerId[row.brokerId] ?? 0) > 0;

	return {
		isNewRow,
		blockRemoveOwner: !isNewRow && hasBrokerInvoices,
	};
};

export const getPaidByOptions = (
	row: BrokerEditabilityRow | null | undefined,
	ctx: BrokerEditabilityContext,
): PaidByOption[] => {
	const { blockRemoveOwner } = getRowReasons(row, ctx);

	const switchToChartererBlocked = row?.paidBy === AccountTypes.OWNER && blockRemoveOwner;

	return [
		{
			label: 'Owner',
			value: AccountTypes.OWNER,
			disabled: false,
		},
		{
			label: 'Charterer',
			value: AccountTypes.CHARTERER,
			disabled: switchToChartererBlocked,
			title: switchToChartererBlocked ? BROKER_INVOICES_EXIST_TOOLTIP : undefined,
		},
	];
};

export type PaidByFieldState = {
	editable: boolean;
	reason: string | null;
};

// Returns whether the paid-by select should be interactive for a given row, and the
// tooltip reason to surface when it is disabled. The field is disabled when no value
// other than the current `paidBy` is selectable.
export const getPaidByFieldState = (
	row: BrokerEditabilityRow | null | undefined,
	ctx: BrokerEditabilityContext,
): PaidByFieldState => {
	const options = getPaidByOptions(row, ctx);
	const otherOption = options.find((o) => o.value !== row?.paidBy);

	if (otherOption == null || !otherOption.disabled) {
		return { editable: true, reason: null };
	}

	return { editable: false, reason: otherOption.title ?? null };
};

export type DeleteState = {
	disabled: boolean;
	reason: string | null;
};

export const getDeleteState = (
	row: BrokerEditabilityRow | null | undefined,
	ctx: BrokerEditabilityContext,
): DeleteState => {
	const { isNewRow, blockRemoveOwner } = getRowReasons(row, ctx);

	if (isNewRow) {
		return { disabled: false, reason: null };
	}

	if (row?.paidBy === AccountTypes.OWNER && blockRemoveOwner) {
		return { disabled: true, reason: BROKER_INVOICES_EXIST_TOOLTIP };
	}

	return { disabled: false, reason: null };
};
